// =============================================================================
// StoreTextSettingsPanel — per-store designer text + font upload limits
// =============================================================================
// Admin view of the two Connection.settings keys the fonts system reads:
//
//   • designerText — Text tool on/off + managed-font allowlist. Merchants can
//     edit this themselves (portal / embedded app); this panel is the admin
//     override for support cases.
//   • fontUpload — merchant upload limits. ADMIN-ONLY by design: merchants
//     can see their effective limits but never raise their own caps.
// =============================================================================

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Input, Spinner } from '@tkpod/ui';

import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

// Recently selected stores (this browser) — shown when the search box is
// empty so a support agent's frequent stores are one click away instead of
// scrolling a dropdown of hundreds of connections.
const RECENT_STORES_KEY = 'tkpod.admin.fonts.recentStores';
const RECENT_STORES_MAX = 5;
/** Search results are capped — typing narrows, scrolling doesn't. */
const STORE_SEARCH_MAX_RESULTS = 8;

function readRecentStoreIds(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_STORES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string')
      : [];
  } catch {
    return [];
  }
}

function pushRecentStoreId(id: string): string[] {
  const next = [id, ...readRecentStoreIds().filter((v) => v !== id)].slice(
    0,
    RECENT_STORES_MAX,
  );
  try {
    window.localStorage.setItem(RECENT_STORES_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode) — recents just don't persist.
  }
  return next;
}

function storeLabel(c: ConnectionRow): string {
  return `${c.organizationName} — ${c.name}${c.shopDomain ? ` (${c.shopDomain})` : ''}`;
}

interface ConnectionRow {
  id: string;
  name: string;
  shopDomain: string | null;
  organizationId: string;
  organizationName: string;
}

interface DesignerTextResponse {
  stored: { enabled?: boolean; allowedFontIds?: string[] };
  resolved: { enabled: boolean; allowedFontIds: string[] | null };
}

interface FontUploadResponse {
  stored: { allowed?: boolean; maxFonts?: number; maxFileSizeMb?: number };
  resolved: { allowed: boolean; maxFonts: number; maxFileSizeMb: number };
}

interface FontOption {
  id: string;
  scope: 'SYSTEM' | 'STORE';
  organizationId: string | null;
  connectionId: string | null;
  family: string;
  displayName: string;
  isActive: boolean;
}

const BASE = '/api/v1/admin/fonts';

export function StoreTextSettingsPanel() {
  const queryClient = useQueryClient();
  const [connectionId, setConnectionId] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [recentIds, setRecentIds] = useState<string[]>(() =>
    readRecentStoreIds(),
  );

  const { data: connections } = useQuery<ConnectionRow[]>({
    queryKey: ['admin', 'fonts', 'connections'],
    queryFn: () => api.get<ConnectionRow[]>(`${BASE}/connections`),
  });

  const selectStore = (id: string) => {
    setConnectionId(id);
    setStoreSearch('');
    setRecentIds(pushRecentStoreId(id));
  };

  // Empty search → the 5 most recently selected stores (ids resolved against
  // the live connection list, so revoked stores silently drop out). Non-empty
  // → case-insensitive contains match on org, store name and domain.
  const storeResults = useMemo(() => {
    const all = connections ?? [];
    const q = storeSearch.trim().toLowerCase();
    if (!q) {
      return recentIds
        .map((id) => all.find((c) => c.id === id))
        .filter((c): c is ConnectionRow => !!c);
    }
    return all.filter((c) => storeLabel(c).toLowerCase().includes(q));
  }, [connections, storeSearch, recentIds]);

  const { data: fonts } = useQuery<FontOption[]>({
    queryKey: ['admin', 'fonts'],
    queryFn: () => api.get<FontOption[]>(BASE),
  });

  const textQuery = useQuery<DesignerTextResponse>({
    queryKey: ['admin', 'fonts', 'designer-text', connectionId],
    queryFn: () =>
      api.get<DesignerTextResponse>(
        `${BASE}/connections/${connectionId}/designer-text`,
      ),
    enabled: !!connectionId,
  });

  const uploadQuery = useQuery<FontUploadResponse>({
    queryKey: ['admin', 'fonts', 'upload-settings', connectionId],
    queryFn: () =>
      api.get<FontUploadResponse>(
        `${BASE}/connections/${connectionId}/upload-settings`,
      ),
    enabled: !!connectionId,
  });

  const selectedConnection = useMemo(
    () => connections?.find((c) => c.id === connectionId) ?? null,
    [connections, connectionId],
  );

  // Fonts this store can see: SYSTEM + its org's STORE fonts.
  const visibleFonts = useMemo(() => {
    if (!fonts || !selectedConnection) return [];
    return fonts.filter(
      (f) =>
        f.scope === 'SYSTEM' ||
        (f.scope === 'STORE' &&
          f.organizationId === selectedConnection.organizationId &&
          (f.connectionId === null || f.connectionId === connectionId)),
    );
  }, [fonts, selectedConnection, connectionId]);

  const updateTextMut = useMutation({
    mutationFn: (body: { enabled?: boolean; allowedFontIds?: string[] }) =>
      api.put<DesignerTextResponse>(
        `${BASE}/connections/${connectionId}/designer-text`,
        body,
      ),
    onSuccess: async () => {
      toast('Designer text settings saved.', 'success');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'fonts', 'designer-text', connectionId],
      });
    },
    onError: (err: Error) => toast(err.message || 'Save failed.', 'error'),
  });

  const updateUploadMut = useMutation({
    mutationFn: (body: {
      allowed?: boolean;
      maxFonts?: number;
      maxFileSizeMb?: number;
    }) =>
      api.put<FontUploadResponse>(
        `${BASE}/connections/${connectionId}/upload-settings`,
        body,
      ),
    onSuccess: async () => {
      toast('Upload limits saved.', 'success');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'fonts', 'upload-settings', connectionId],
      });
    },
    onError: (err: Error) => toast(err.message || 'Save failed.', 'error'),
  });

  return (
    <Card>
      <CardHeader
        title="Per-store text settings"
        description="Text tool availability, font allowlist and merchant upload limits for one store. Merchants manage the text settings themselves — this is the admin override."
      />

      <div className="mt-4 max-w-md space-y-2">
        <Input
          label="Store"
          type="search"
          placeholder="Search by organization, store name or domain…"
          value={storeSearch}
          onChange={(e) => setStoreSearch(e.target.value)}
        />
        {selectedConnection && (
          <div className="flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-surface-subtle px-3 py-2">
            <span className="truncate text-sm font-medium text-text-primary">
              {storeLabel(selectedConnection)}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConnectionId('')}
            >
              Change
            </Button>
          </div>
        )}
        {!selectedConnection && (
          <div className="rounded-md border border-border-subtle">
            {storeResults.length > 0 && (
              <>
                <p className="border-b border-border-subtle px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-text-tertiary">
                  {storeSearch.trim() ? 'Results' : 'Recent stores'}
                </p>
                <ul>
                  {storeResults
                    .slice(0, STORE_SEARCH_MAX_RESULTS)
                    .map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="w-full truncate px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-subtle"
                          onClick={() => selectStore(c.id)}
                        >
                          {storeLabel(c)}
                        </button>
                      </li>
                    ))}
                </ul>
                {storeResults.length > STORE_SEARCH_MAX_RESULTS && (
                  <p className="border-t border-border-subtle px-3 py-1.5 text-xs text-text-tertiary">
                    {storeResults.length - STORE_SEARCH_MAX_RESULTS} more —
                    keep typing to narrow down.
                  </p>
                )}
              </>
            )}
            {storeResults.length === 0 && (
              <p className="px-3 py-2 text-sm text-text-tertiary">
                {storeSearch.trim()
                  ? 'No stores match this search.'
                  : 'Type to search stores. Recently selected stores appear here.'}
              </p>
            )}
          </div>
        )}
      </div>

      {connectionId && (textQuery.isLoading || uploadQuery.isLoading) && (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      )}

      {connectionId && textQuery.data && (
        <DesignerTextForm
          key={`text-${connectionId}`}
          data={textQuery.data}
          fonts={visibleFonts}
          pending={updateTextMut.isPending}
          onSave={(body) => updateTextMut.mutate(body)}
        />
      )}

      {connectionId && uploadQuery.data && (
        <UploadLimitsForm
          key={`upload-${connectionId}`}
          data={uploadQuery.data}
          pending={updateUploadMut.isPending}
          onSave={(body) => updateUploadMut.mutate(body)}
        />
      )}
    </Card>
  );
}

function DesignerTextForm({
  data,
  fonts,
  pending,
  onSave,
}: {
  data: DesignerTextResponse;
  fonts: FontOption[];
  pending: boolean;
  onSave: (body: { enabled: boolean; allowedFontIds: string[] }) => void;
}) {
  const [enabled, setEnabled] = useState(data.resolved.enabled);
  const [allowlist, setAllowlist] = useState<Set<string>>(
    new Set(data.resolved.allowedFontIds ?? []),
  );
  const filterActive = allowlist.size > 0;

  const toggleFont = (id: string) => {
    setAllowlist((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <fieldset className="mt-6 space-y-3 rounded-lg border border-border-subtle p-4">
      <legend className="px-1 text-sm font-semibold text-text-primary">
        Designer text tool
      </legend>
      <label className="flex items-center gap-2 text-sm text-text-primary">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 rounded border-border-subtle"
        />
        Text tool enabled
      </label>
      <div>
        <p className="text-sm font-medium text-text-secondary">
          Font allowlist{' '}
          <span className="font-normal text-text-tertiary">
            (none checked = all visible fonts offered; SYSTEM and builtin
            families always stay available)
          </span>
        </p>
        <ul className="mt-2 grid grid-cols-2 gap-1 md:grid-cols-3">
          {fonts.map((f) => (
            <li key={f.id}>
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  checked={allowlist.has(f.id)}
                  onChange={() => toggleFont(f.id)}
                  className="h-4 w-4 rounded border-border-subtle"
                />
                <span className={f.isActive ? '' : 'line-through opacity-60'}>
                  {f.displayName}
                </span>
                <span className="text-xs text-text-tertiary">
                  {f.scope === 'SYSTEM' ? 'System' : 'Store'}
                </span>
              </label>
            </li>
          ))}
          {fonts.length === 0 && (
            <li className="text-sm text-text-tertiary">
              No managed fonts visible to this store.
            </li>
          )}
        </ul>
        {filterActive && (
          <p className="mt-1 text-xs text-text-tertiary">
            {allowlist.size} font(s) whitelisted.
          </p>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        disabled={pending}
        onClick={() =>
          onSave({ enabled, allowedFontIds: Array.from(allowlist) })
        }
      >
        {pending ? 'Saving…' : 'Save text settings'}
      </Button>
    </fieldset>
  );
}

function UploadLimitsForm({
  data,
  pending,
  onSave,
}: {
  data: FontUploadResponse;
  pending: boolean;
  onSave: (body: {
    allowed: boolean;
    maxFonts: number;
    maxFileSizeMb: number;
  }) => void;
}) {
  const [allowed, setAllowed] = useState(data.resolved.allowed);
  const [maxFonts, setMaxFonts] = useState(String(data.resolved.maxFonts));
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(
    String(data.resolved.maxFileSizeMb),
  );

  const parsedMaxFonts = Number.parseInt(maxFonts, 10);
  const parsedMaxMb = Number.parseInt(maxFileSizeMb, 10);
  const valid =
    Number.isInteger(parsedMaxFonts) &&
    parsedMaxFonts >= 0 &&
    Number.isInteger(parsedMaxMb) &&
    parsedMaxMb >= 1;

  return (
    <fieldset className="mt-6 space-y-3 rounded-lg border border-border-subtle p-4">
      <legend className="px-1 text-sm font-semibold text-text-primary">
        Merchant upload limits
      </legend>
      <label className="flex items-center gap-2 text-sm text-text-primary">
        <input
          type="checkbox"
          checked={allowed}
          onChange={(e) => setAllowed(e.target.checked)}
          className="h-4 w-4 rounded border-border-subtle"
        />
        Merchant font uploads allowed
      </label>
      <div className="flex gap-4">
        <div className="w-40">
          <Input
            label="Max fonts"
            type="number"
            min={0}
            value={maxFonts}
            onChange={(e) => setMaxFonts(e.target.value)}
          />
        </div>
        <div className="w-40">
          <Input
            label="Max file size (MB)"
            type="number"
            min={1}
            value={maxFileSizeMb}
            onChange={(e) => setMaxFileSizeMb(e.target.value)}
          />
        </div>
      </div>
      <p className="text-xs text-text-tertiary">
        Overrides the FONT_UPLOAD_* platform defaults for this store. Max
        fonts counts active store families only. Platform ceilings still
        apply (100 fonts / 30 MB).
      </p>
      <Button
        type="button"
        size="sm"
        disabled={pending || !valid}
        onClick={() =>
          onSave({ allowed, maxFonts: parsedMaxFonts, maxFileSizeMb: parsedMaxMb })
        }
      >
        {pending ? 'Saving…' : 'Save upload limits'}
      </Button>
    </fieldset>
  );
}
